import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Lock, Smartphone, X } from "lucide-react";
import { UserMenu } from "./UserMenu";

export const Sidebar = ({
  navItems, isActive, collapsed, sidebarOpen, setSidebarOpen, hasFeature,
  unreadMessages, unreadPortalMessages, theme, user, navigate, handleLogout,
}) => (
  <aside className={`
    fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-all duration-200 ease-in-out
    lg:translate-x-0 lg:relative lg:inset-auto lg:shrink-0
    ${collapsed ? 'lg:w-20' : 'lg:w-64'}
    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
  `}>
    <div className="flex flex-col h-full">
      {/* Logo - visible on both mobile and desktop */}
      <div className="flex items-center justify-center py-5 border-b border-border">
        <img 
          src={theme === "dark" ? "/logo-dark.png?v=3" : "/logo-light.png?v=3"} 
          alt="SkilledShark" 
          className={`h-10 w-auto object-contain ${collapsed ? 'lg:hidden' : ''}`}
        />
        <img
          src="/logo-shark.png?v=3"
          alt="SkilledShark"
          className={`h-9 w-auto object-contain ${collapsed ? 'hidden lg:block' : 'hidden'}`}
        />
        {/* Close button for mobile */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="ml-auto lg:hidden h-8 w-8"
          onClick={() => setSidebarOpen(false)}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const locked = item.feature && !hasFeature(item.feature);

          if (locked) {
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                title={collapsed ? item.label : undefined}
                aria-label={collapsed ? item.label : undefined}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium text-muted-foreground/50 hover:bg-muted/40 transition-colors group ${collapsed ? 'lg:justify-center lg:gap-0 lg:px-0' : ''}`}
              >
                <Icon className="h-5 w-5 opacity-50 shrink-0" />
                <span className={`opacity-60 ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                <Lock className={`h-3.5 w-3.5 ml-auto text-amber-500/70 group-hover:text-amber-500 transition-colors ${collapsed ? 'lg:hidden' : ''}`} />
              </Link>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              title={collapsed ? item.label : undefined}
              aria-label={collapsed ? item.label : undefined}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`
                relative flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors
                ${collapsed ? 'lg:justify-center lg:gap-0 lg:px-0' : ''}
                ${item.label === "TradeIQ"
                  ? (isActive(item.path)
                    ? 'bg-cyan-500/15 text-cyan-400 ring-1 ring-cyan-500/30'
                    : 'text-cyan-500/70 hover:bg-cyan-500/10 hover:text-cyan-400')
                  : (isActive(item.path) 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground')
                }
              `}
            >
              <Icon className={`h-5 w-5 shrink-0 ${item.label === "TradeIQ" ? "text-cyan-400" : ""}`} />
              <span className={collapsed ? 'lg:hidden' : ''}>{item.label}</span>
              {/* Collapsed unread dot */}
              {collapsed && ((item.label === "Messages" && unreadMessages > 0) || (item.label === "Customers" && unreadPortalMessages > 0)) && (
                <span className="hidden lg:block absolute top-1.5 right-2 h-2 w-2 rounded-full bg-brand-500" />
              )}
              {item.label === "Messages" && unreadMessages > 0 && (
                <span className={`ml-auto text-[10px] font-bold h-5 min-w-[20px] px-1.5 rounded-full flex items-center justify-center ${collapsed ? 'lg:hidden' : ''} ${
                  isActive(item.path) 
                    ? 'bg-white text-primary' 
                    : 'bg-brand-500 text-white'
                }`}>
                  {unreadMessages > 99 ? "99+" : unreadMessages}
                </span>
              )}
              {item.label === "Customers" && unreadPortalMessages > 0 && (
                <span className={`ml-auto bg-brand-500 text-white text-[10px] font-bold h-5 min-w-[20px] px-1.5 rounded-full flex items-center justify-center ${collapsed ? 'lg:hidden' : ''}`} data-testid="crm-unread-badge">
                  {unreadPortalMessages > 99 ? "99+" : unreadPortalMessages}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Mobile App Link */}
      <div className={`px-4 py-3 mx-3 mb-2 bg-brand-500/10 rounded-lg border border-brand-500/20 ${collapsed ? 'lg:px-0 lg:mx-2 lg:flex lg:justify-center' : ''}`}>
        <Link 
          to="/mobile" 
          title={collapsed ? "Get Mobile App" : undefined}
          className={`flex items-center gap-3 text-sm ${collapsed ? 'lg:justify-center' : ''}`}
          onClick={() => setSidebarOpen(false)}
        >
          <div className="h-8 w-8 rounded-lg bg-brand-500 flex items-center justify-center shrink-0">
            <Smartphone className="h-4 w-4 text-white" />
          </div>
          <div className={`flex-1 ${collapsed ? 'lg:hidden' : ''}`}>
            <p className="font-medium text-foreground">Get Mobile App</p>
            <p className="text-xs text-muted-foreground">Add to home screen</p>
          </div>
        </Link>
      </div>

      <UserMenu user={user} collapsed={collapsed} navigate={navigate} handleLogout={handleLogout} />
    </div>
  </aside>
);
